// ============================================================
// systems/save.js — Save / Load system (localStorage + Supabase cloud)
// ============================================================
import { state } from '../state.js';
import { dom } from '../dom.js';
import { BADGE_CHARACTERS } from '../constants.js';
import { updateShopUI, spawnAutoMouse, getAutoInterval } from './shop.js';
import { setCharacterImage } from './menu.js';
import { checkPhaseTransitions } from '../phases.js';
import { supabase } from './supabase.js';

// ---- Save ----

export function saveGame() {
  const data = {
    score:             state.score,
    clickPower:        state.clickPower,
    clickPowerCost:    state.clickPowerCost,
    clickPowerLevel:   state.clickPowerLevel,
    autoClickerCount:  state.autoClickerCount,
    autoClickerCost:   state.autoClickerCost,
    autoSpeedLevel:    state.autoSpeedLevel,
    autoSpeedCost:     state.autoSpeedCost,
    megaClickLevel:    state.megaClickLevel,
    megaClickCost:     state.megaClickCost,
    selectedCharacter: state.selectedCharacter,
    badges:            state.badges,
    musicSource:       state.musicSource,
    sfxEnabled:        state.sfxEnabled,
    musicOn:           dom.toggleMusic.classList.contains('on'),
    caineUnlocked:     state.caineUnlocked,
    secretUnlocked:    state.secretUnlocked,
    selectedChar:      state.selectedChar,
    timestamp:         Date.now(),
  };
  try { localStorage.setItem('smoothClicker_save', JSON.stringify(data)); }
  catch (e) { console.warn('[save] localStorage write failed:', e?.message || e); }

  // Sync to cloud if logged in
  if (state.onlineMode && state.supabaseUser) {
    saveToCloud();
  }
}

async function saveToCloud() {
  try {
    const { error } = await supabase.from('game_saves').upsert({
      id:                 state.supabaseUser.id,
      score:              state.score,
      click_power:        state.clickPower,
      click_power_cost:   state.clickPowerCost,
      click_power_level:  state.clickPowerLevel,
      auto_clicker_count: state.autoClickerCount,
      auto_clicker_cost:  state.autoClickerCost,
      auto_speed_level:   state.autoSpeedLevel,
      auto_speed_cost:    state.autoSpeedCost,
      mega_click_level:   state.megaClickLevel,
      mega_click_cost:    state.megaClickCost,
      selected_character: state.selectedCharacter,
      badges:             state.badges,
      caine_unlocked:     state.caineUnlocked,
      music_source:       state.musicSource,
      sfx_enabled:        state.sfxEnabled,
      high_score:         state.highScore,
      updated_at:         new Date().toISOString(),
    });
    if (error) console.warn('[save] cloud upsert error:', error.message);
  } catch (e) {
    console.warn('[save] cloud upsert threw:', e?.message || e);
  }
}

// Forward-declared phase activation functions — set via provideSaveDeps()
let activateBombMode;
let activateFrozenMode;
let activatePhase3;
let activatePhase4;
let activatePhase5;

/**
 * Provide external phase-activation functions that loadGame needs.
 * Called once at bootstrap to break circular dependency chains.
 */
export function provideSaveDeps(deps) {
  activateBombMode   = deps.activateBombMode;
  activateFrozenMode = deps.activateFrozenMode;
  activatePhase3     = deps.activatePhase3;
  activatePhase4     = deps.activatePhase4;
  activatePhase5     = deps.activatePhase5;
}

// ---- Load ----

/**
 * Load game data. Tries cloud first (if logged in), falls back to localStorage.
 */
export async function loadGame() {
  let data = null;
  let isCloud = false;

  // Try cloud first if logged in
  if (state.supabaseUser) {
    try {
      const { data: cloudData, error } = await supabase
        .from('game_saves')
        .select('*')
        .eq('id', state.supabaseUser.id)
        .maybeSingle();
      if (error) {
        console.warn('[load] cloud fetch error:', error.message);
      } else if (cloudData) {
        data = cloudData;
        isCloud = true;
      }
    } catch (e) {
      console.warn('[load] cloud fetch threw:', e?.message || e);
    }
  }

  // Fallback to localStorage
  if (!data) {
    try {
      const raw = localStorage.getItem('smoothClicker_save');
      if (raw) data = JSON.parse(raw);
    } catch (e) {
      console.warn('[load] localStorage parse failed:', e?.message || e);
    }
  }

  if (!data) return;
  applyLoadedData(data, isCloud);
}

function applyLoadedData(data, isCloud) {
  // Map fields — cloud uses snake_case, localStorage uses camelCase
  if (isCloud) {
    state.score            = data.score || 0;
    state.clickPower       = data.click_power || 1;
    state.clickPowerCost   = data.click_power_cost || 50;
    state.clickPowerLevel  = data.click_power_level || 0;
    state.autoClickerCount = data.auto_clicker_count || 0;
    state.autoClickerCost  = data.auto_clicker_cost || 250;
    state.autoSpeedLevel   = data.auto_speed_level || 0;
    state.autoSpeedCost    = data.auto_speed_cost || 500;
    state.megaClickLevel   = data.mega_click_level || 0;
    state.megaClickCost    = (data.mega_click_level > 0) ? (data.mega_click_cost || 1000) : 1000;
    state.selectedCharacter = data.selected_character || 'tomni';
    state.badges           = Object.assign(state.badges, data.badges || {});
    state.sfxEnabled       = data.sfx_enabled !== false;
    state.musicSource      = data.music_source || 'mp3';
    state.caineUnlocked    = data.caine_unlocked || false;
    state.highScore        = data.high_score || 0;
  } else {
    state.score            = data.score || 0;
    state.clickPower       = data.clickPower || 1;
    state.clickPowerCost   = data.clickPowerCost || 50;
    state.clickPowerLevel  = data.clickPowerLevel || 0;
    state.autoClickerCount = data.autoClickerCount || 0;
    state.autoClickerCost  = data.autoClickerCost || 250;
    state.autoSpeedLevel   = data.autoSpeedLevel || 0;
    state.autoSpeedCost    = data.autoSpeedCost || 500;
    state.megaClickLevel   = data.megaClickLevel || 0;
    state.megaClickCost    = (data.megaClickLevel > 0) ? (data.megaClickCost || 1000) : 1000;
    if (data.selectedCharacter) state.selectedCharacter = data.selectedCharacter;
    if (data.badges) state.badges = Object.assign(state.badges, data.badges);
    state.sfxEnabled       = data.sfxEnabled !== false;
    state.musicSource      = data.musicSource || 'mp3';
    state.caineUnlocked    = data.caineUnlocked || false;
    state.secretUnlocked   = data.secretUnlocked || false;
    state.selectedChar     = data.selectedChar || 'zinger';
  }

  // Update UI
  setCharacterImage(state.selectedCharacter);
  dom.scoreEl.textContent = state.score;
  updateShopUI();

  // Also check badges — if all escaped, force unlock Waine
  if (!state.caineUnlocked) {
    const allEscaped = BADGE_CHARACTERS.every(c => state.badges[c.id] === true);
    if (allEscaped) state.caineUnlocked = true;
  }

  if (state.caineUnlocked && dom.charWaineSelect) {
    dom.charWaineSelect.classList.remove('locked');
    const lock = dom.charWaineSelect.querySelector('.char-lock');
    if (lock) lock.style.display = 'none';
  }

  if (state.secretUnlocked && dom.charSecretSelect) {
    dom.charSecretSelect.classList.remove('locked');
    const lock = dom.charSecretSelect.querySelector('.char-lock');
    if (lock) lock.style.display = 'none';
    const name = dom.charSecretSelect.querySelector('.char-name');
    if (name) name.textContent = 'ELCHICOQUESEPARECEMUCHOATOMNI';
    const img = dom.charSecretSelect.querySelector('img');
    if (img) img.alt = 'ELCHICOQUESEPARECEMUCHOATOMNI';
  }

  // Restore SFX toggle
  dom.toggleSfx.classList.toggle('on', state.sfxEnabled);

  // Restore music source buttons
  if (state.musicSource === 'og') {
    dom.srcOg.classList.add('active');
    dom.srcMp3.classList.remove('active');
  }

  // Restore music toggle (only from localStorage — cloud doesn't store musicOn)
  if (!isCloud && data.musicOn === false) {
    dom.toggleMusic.classList.remove('on');
  }

  // Update high score from cloud
  if (isCloud && state.highScore) {
    localStorage.setItem('smoothClicker_highScore', state.highScore.toString());
  }

  // Clear any existing auto-clicker intervals & sprites before rebuilding —
  // loadGame() can be re-invoked (e.g. on SIGNED_IN after initAuth), and
  // without this the intervals stack and the mice duplicate on screen.
  state.autoClickerIntervals.forEach(id => clearInterval(id));
  state.autoClickerIntervals = [];
  state.autoMouseElements.forEach(m => m.el.remove());
  state.autoMouseElements = [];

  // Respawn auto clicker mice
  for (let i = 0; i < state.autoClickerCount; i++) {
    spawnAutoMouse();
    const intId = setInterval(() => {
      if (state.dead || state.paused || state.escaped) return;
      state.score += 1;
      dom.scoreEl.textContent = state.score;
      updateShopUI();
      checkPhaseTransitions();
    }, getAutoInterval());
    state.autoClickerIntervals.push(intId);
  }

  // Activate phases based on score
  if (state.score >= 500)  activateBombMode();
  if (state.score >= 1500) activateFrozenMode();
  if (state.score >= 3500) activatePhase3();
  if (state.score >= 6000 && state.score < 7500) activatePhase4();
  if (state.score >= 7500) activatePhase5();
}

// ---- Auto-save ----

export function initAutoSave() {
  // Auto-save every 5 seconds
  setInterval(saveGame, 5000);

  // Save on page close
  window.addEventListener('beforeunload', saveGame);
}
